import {
	useQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type {
	UseQueryResult,
	UseMutationResult,
} from "@tanstack/react-query";
import { arrayMove } from "@dnd-kit/sortable";
import type { ComplianceQuestion } from "newui/features/Settings/types/ComplianceQuestion.type";
import {
	getComplianceQuestions,
	createComplianceQuestion,
	updateComplianceQuestion,
	deleteComplianceQuestion,
	reorderComplianceQuestions,
} from "./compliance-questions-api";

const STALE_TIME = 30_000;

export const complianceQuestionKeys = {
	list: (companyId: number | undefined) =>
		["compliance-questions", companyId] as const,
};

export interface UpdateComplianceQuestionVars {
	id: number;
	question: string;
}

/** Fetch the list of global compliance questions. */
export const useComplianceQuestions = (
	companyId: number | undefined
): UseQueryResult<ComplianceQuestion[]> =>
	useQuery({
		queryKey: complianceQuestionKeys.list(companyId),
		queryFn: getComplianceQuestions,
		staleTime: STALE_TIME,
	});

/** Create a question (POST). */
export const useCreateComplianceQuestion = (
	companyId: number | undefined
): UseMutationResult<ComplianceQuestion, Error, string> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (question: string) => createComplianceQuestion(question),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: complianceQuestionKeys.list(companyId),
			});
		},
	});
};

/** Update a question (PUT /{id}). */
export const useUpdateComplianceQuestion = (
	companyId: number | undefined
): UseMutationResult<ComplianceQuestion, Error, UpdateComplianceQuestionVars> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, question }: UpdateComplianceQuestionVars) =>
			updateComplianceQuestion(id, question),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: complianceQuestionKeys.list(companyId),
			});
		},
	});
};

/** Delete a question (DELETE /{id}). */
export const useDeleteComplianceQuestion = (
	companyId: number | undefined
): UseMutationResult<void, Error, number> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (id: number) => deleteComplianceQuestion(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: complianceQuestionKeys.list(companyId),
			});
		},
	});
};

interface ReorderContext {
	previous: ComplianceQuestion[] | undefined;
}

/**
 * Reorder questions (PUT /reorder) with an optimistic update.
 * On error the cached list rolls back to the pre-drag order.
 */
export const useReorderComplianceQuestions = (
	companyId: number | undefined
): UseMutationResult<ComplianceQuestion[], Error, number[], ReorderContext> => {
	const queryClient = useQueryClient();
	const queryKey = complianceQuestionKeys.list(companyId);

	return useMutation<ComplianceQuestion[], Error, number[], ReorderContext>({
		mutationFn: (ids: number[]) => reorderComplianceQuestions(ids),
		onMutate: async (ids: number[]) => {
			await queryClient.cancelQueries({ queryKey });
			const previous =
				queryClient.getQueryData<ComplianceQuestion[]>(queryKey);

			if (previous) {
				const byId = new Map(previous.map((q) => [q.id, q]));
				const optimistic = ids
					.map((id) => byId.get(id))
					.filter((q): q is ComplianceQuestion => Boolean(q))
					.map((q, index) => ({ ...q, sort_order: index + 1 }));
				queryClient.setQueryData<ComplianceQuestion[]>(queryKey, optimistic);
			}

			return { previous };
		},
		onError: (_error, _ids, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
};

export { arrayMove };
